import { Box, Heading, LogoLoader, StatusBadge, TextBlock } from "@bcc-ui/ui-components";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { toLockedDeviceAssets } from "./custom-views/tabs/risk/cards/risk-factors-card/lockedDevices";
import style from "components/asset-management-system/asset-side-panel.scss";
import { deduceRiskLevelValue, getRiskLevelStatus, LockState } from "components/asset-management-system/assetRiskUtils";
import { RemediationStepCard } from "components/asset-management-system/RemediationStepCard";
import { Asset, assetService } from "services/asset-management/AssetService";
import { isStringBlank, isStringNotBlank } from "utils/commonFunctions";
import { logger } from "utils/logging";

import testIds from "testIds.json";

interface Props {
    assetUuid: string;
}

interface RemediationStep {
    id: string;
    testId: string;
    titleKey: string;
    descriptionKey: string;
    helperTextKey?: string;
    actionLabelKey?: string;
    completed: boolean;
    onAction?: () => void;
}

export function AssetRiskView(props: Props) {
    const { t } = useTranslation();
    const [loading, setLoading] = React.useState(false);
    const [asset, setAsset] = React.useState<Asset>();

    useEffect(() => {
        setLoading(true);
        const abortController = new AbortController();
        assetService
            .fetchAssetByUuid(props.assetUuid, abortController)
            .then((response) => {
                if (!abortController.signal.aborted) {
                    setAsset(response);
                }
            })
            .catch((error) => {
                if (!abortController.signal.aborted) {
                    logger.error("Failed to fetch asset details for risk level view.", error);
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                }
            });
        return () => abortController.abort();
    }, [props.assetUuid]);

    if (loading) {
        return (
            <Box horizontalAlignment="Center" verticalAlignment="Center" className={style.loadingState}>
                <LogoLoader />
            </Box>
        );
    }

    if (asset === undefined) {
        return null;
    }

    const getLockState = (asset: Asset): string => {
        const { lockDetails } = toLockedDeviceAssets([asset])[0] ?? {};
        return lockDetails?.length
            ? lockDetails
                  .map((detail) =>
                      t(
                          `AssetManagement.risk.riskOverviewTable.lockLabel.${detail.lockPath}.${detail.rawValue.toUpperCase()}`,
                          {
                              defaultValue: detail.rawValue,
                          }
                      )
                  )
                  .join(", ")
            : "-";
    };

    const getErasureReportAvailability = (hasSuccessfulErasure?: boolean): string => {
        switch (hasSuccessfulErasure) {
            case true:
                return t("Common.available");
            default:
                return t("Common.dataNotAvailable");
        }
    };

    const remediationSteps: RemediationStep[] = asset.isDataBearing
        ? [
              {
                  id: "uploadErasureReport",
                  testId: testIds.workArea.asset.sidePanel.riskTab.uploadErasureReportStep,
                  titleKey: "AssetManagement.sidePanel.riskLevel.remediation.uploadErasureReport.title",
                  descriptionKey: "AssetManagement.sidePanel.riskLevel.remediation.missingErasureReport",
                  helperTextKey: asset.hasSuccessfulErasure
                      ? ""
                      : "AssetManagement.sidePanel.riskLevel.remediation.uploadErasureReport.helperText",
                  completed: !!asset.hasSuccessfulErasure,
              },
              {
                  id: "unlockDevice",
                  testId: testIds.workArea.asset.sidePanel.riskTab.unlockDeviceStep,
                  titleKey: "AssetManagement.sidePanel.riskLevel.remediation.unlockDevice.title",
                  descriptionKey:
                      asset.lockState === LockState.NOT_AVAILABLE
                          ? "AssetManagement.sidePanel.riskLevel.remediation.unlockDevice.notAvailable"
                          : "AssetManagement.sidePanel.riskLevel.remediation.locked",
                  completed: asset.lockState === LockState.UNLOCKED,
              },
          ]
        : [];

    const completedStepsCount = remediationSteps.filter((step) => step.completed).length;
    const addRiskDetails = () => {
        return asset.isDataBearing && isStringBlank(asset.parentUuid);
    };

    const generateDescription = () => {
        if (addRiskDetails()) {
            return t("AssetManagement.sidePanel.riskLevel.description");
        }
        if (isStringNotBlank(asset.parentUuid)) {
            return t("AssetManagement.sidePanel.riskLevel.noRiskScoreSubAssetDescription");
        }
        return t("AssetManagement.sidePanel.riskLevel.noRiskScoreNonDataBearingDescription");
    };

    return (
        <Box columnLayout gap="Xs">
            <div className={style.riskLevelBadge}>
                <StatusBadge
                    value={deduceRiskLevelValue(t, asset.riskLevel)}
                    status={getRiskLevelStatus(asset.riskLevel)}
                />
            </div>
            <TextBlock disableBottomSpacing>{generateDescription()}</TextBlock>
            {addRiskDetails() && (
                <>
                    <TextBlock disableBottomSpacing>{t("AssetManagement.sidePanel.tabs.riskLevel")}</TextBlock>
                    <Box gap={"Xxs"}>
                        <TextBlock disableBottomSpacing className={style.detailLabel}>
                            {t("AssetManagement.dataBearingFilter.dataBearing")}
                        </TextBlock>
                        <TextBlock disableBottomSpacing className={style.detailValue}>
                            {asset.isDataBearing ? t("Common.true") : t("Common.false")}
                        </TextBlock>
                    </Box>
                    <Box gap={"Xxs"}>
                        <TextBlock disableBottomSpacing className={style.detailLabel}>
                            {t("AssetManagement.sidePanel.riskLevel.erasureReport")}
                        </TextBlock>
                        <TextBlock disableBottomSpacing className={style.detailValue}>
                            {getErasureReportAvailability(asset.hasSuccessfulErasure)}
                        </TextBlock>
                    </Box>
                    <Box gap={"Xxs"}>
                        <TextBlock disableBottomSpacing className={style.detailLabel}>
                            {t("AssetManagement.sidePanel.riskLevel.lockState")}
                        </TextBlock>
                        <TextBlock disableBottomSpacing className={style.detailValue}>
                            {getLockState(asset)}
                        </TextBlock>
                    </Box>
                    {remediationSteps.length > 0 && (
                        <Box columnLayout gap="Xxs" testId={testIds.workArea.asset.sidePanel.riskTab.itself}>
                            <hr className={style.sectionDivider} />
                            <Heading variant="SUBTITLE_2" className={style.sectionTitle}>
                                {t("AssetManagement.sidePanel.riskLevel.remediationStepsTitle")}
                            </Heading>
                            <Box justifyContent="SpaceBetween" verticalAlignment="Center">
                                <TextBlock disableBottomSpacing>
                                    {t("AssetManagement.sidePanel.riskLevel.remediation.sectionSubtitle")}
                                </TextBlock>
                                <TextBlock disableBottomSpacing className={style.remediationProgressText}>
                                    {t("AssetManagement.sidePanel.riskLevel.remediation.progress", {
                                        done: completedStepsCount,
                                        total: remediationSteps.length,
                                    })}
                                </TextBlock>
                            </Box>
                            {remediationSteps.map((step) => (
                                <RemediationStepCard
                                    key={step.id}
                                    testId={step.testId}
                                    titleKey={step.titleKey}
                                    descriptionKey={step.descriptionKey}
                                    helperTextKey={step.helperTextKey}
                                    actionLabelKey={step.actionLabelKey}
                                    completed={step.completed}
                                    onAction={step.onAction}
                                />
                            ))}
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
}
